import React, { useState, useEffect } from 'react';
import { Button, Card, notification, Slider, Select, Input } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import {
	ArrowRight,
	Settings,
	Sparkles,
	CheckCircle,
	MessageSquare,
	Phone,
	MessageCircle,
	Users,
	Calendar,
	Cog,
} from 'lucide-react';
import './QuickSetupQuestions.scss';
import { useMutation } from '@tanstack/react-query';
import { checkConfigurationStatus } from '@/api/user';
import { UserAccount } from '@/types/TAccounts';
import posthog from 'posthog-js';
import { useClarity } from 'src/Hooks/useClarity';
import {
	EBusinessLocations,
	EBusinessRevenue,
	ECallVolume,
	ReferralSource,
} from 'src/enums/ESmbDataCollection';

interface QuickSetupQuestionsProps {
	businessName?: string;
	onSubmit: (values: QuickSetupFormValues) => void;
	isLoading?: boolean;
	isAnalyzing?: boolean;
	features?: any[];
	debrand?: boolean;
	resellerLogoUrl?: string;
	isComprehensiveScrape?: boolean;
	handleError?: () => void;
	tempKey: string;
	loggedInUser: UserAccount;
	userId: string | undefined;
}

export interface QuickSetupFormValues {
	callVolume?: ECallVolume;
	businessLocations?: EBusinessLocations;
	businessRevenue?: EBusinessRevenue;
	interestedFeatures: string[];
	referralSource?: string;
}

// DO NOT CHANGE, WE RELY ON THIS DATA IN THIS FORMAT DOWNSTREAM
const callVolumeOptions: { label: string; value: ECallVolume }[] = [
	{ label: '1-10 calls', value: '1-10' },
	{ label: '11-25 calls', value: '11-25' },
	{ label: '26-50 calls', value: '26-50' },
	{ label: '51-100 calls', value: '51-100' },
	{ label: '100+ calls', value: '100+' },
];

// DO NOT CHANGE, WE RELY ON THIS DATA IN THIS FORMAT DOWNSTREAM
const locationOptions: { label: string; value: EBusinessLocations }[] = [
	{ label: '1 location', value: '1' },
	{ label: '2-5 locations', value: '2-5' },
	{ label: '6-10 locations', value: '6-10' },
	{ label: '11-20 locations', value: '11-20' },
	{ label: '20+ locations', value: '20+' },
];

// DO NOT CHANGE, WE RELY ON THIS DATA IN THIS FORMAT DOWNSTREAM
const revenueOptions: { label: string; value: EBusinessRevenue }[] = [
	{ label: 'Under $100K', value: '<100K' },
	{ label: '$100K-$500K', value: '100K-500K' },
	{ label: '$500K-$1M', value: '500K-1M' },
	{ label: '$1M-$5M', value: '1M-5M' },
	{ label: '$5M-$10M', value: '5M-10M' },
	{ label: '$10M-$50M', value: '10M-50M' },
	{ label: '$50M+', value: '50M+' },
	{ label: 'Prefer not to say', value: 'private' },
];

const referralSourceOptions = [
	{ value: 'Google', label: 'Google' },
	{ value: 'ChatGPT / AI', label: 'ChatGPT / AI' },
	{ value: 'Facebook', label: 'Facebook' },
	{ value: 'Instagram', label: 'Instagram' },
	{ value: 'Youtube', label: 'Youtube' },
	{ value: 'Friend or Colleague', label: 'Friend or Colleague' },
	{
		value: "One of our AI Receptionist's",
		label: "One of our AI Receptionist's",
	},
	{ value: 'Other', label: 'Other' },
];

const featureOptions = [
	{
		label: 'Answering FAQ',
		value: 'answering_faq',
		icon: MessageSquare,
		description: 'Handle customer inquiries and provide business information',
	},
	{
		label: 'Gather customer info',
		value: 'gather_customer_info',
		icon: Users,
		description: 'Collect and organize customer contact details',
	},
	{
		label: 'Call transfers',
		value: 'call_transfers',
		icon: Phone,
		description: 'Transfer calls to appropriate departments or staff',
	},
	{
		label: 'Texting links',
		value: 'texting_links',
		icon: MessageCircle,
		description: 'Send helpful links and resources via text',
	},
	{
		label: 'Booking appointments',
		value: 'booking_appointments',
		icon: Calendar,
		description: 'Schedule and manage customer appointments',
	},
	{
		label: 'API integrations',
		value: 'api_integrations',
		icon: Cog,
		description: 'Connect with your existing business tools',
	},
];

// Custom Slider Component using Ant Design
interface SliderProps {
	options: { label: string; value: string }[];
	selectedIndex: number;
	onChange: (index: number) => void;
	className?: string;
	isMobile?: boolean;
}

const CustomSlider: React.FC<SliderProps> = ({
	options,
	selectedIndex,
	onChange,
	className = '',
	isMobile = false,
}) => {
	const marks = options.reduce((acc, option, index) => {
		const isFirst = index === 0;
		const isLast = index === options.length - 1;

		let transform = 'translateX(-50%)'; // Default center alignment
		if (isFirst) {
			transform = 'translateX(-5%)'; // Left align for first item
		} else if (isLast) {
			transform = 'translateX(-95%)'; // Right align for last item
		}

		acc[index] = {
			style: {
				color: index === selectedIndex ? '#3b82f6' : '#6b7280',
				fontWeight: index === selectedIndex ? 600 : 400,
				fontSize: '12px',
				whiteSpace: 'nowrap',
				transform,
			},
			label: isMobile ? (index === selectedIndex ? option.label : '') : option.label,
		};
		return acc;
	}, {} as any);

	return (
		<div className={className}>
			<Slider
				min={0}
				max={options.length - 1}
				step={1}
				value={selectedIndex}
				onChange={onChange}
				marks={marks}
				tooltip={{ open: false }}
				styles={{
					rail: {
						backgroundColor: '#e5e7eb',
					},
					handle: {
						backgroundColor: '#3b82f6',
						borderColor: '#3b82f6',
						boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
					},
					track: {
						backgroundColor: '#3b82f6',
					},
				}}
				className="!mb-4"
			/>
		</div>
	);
};

const QuickSetupQuestions: React.FC<QuickSetupQuestionsProps> = ({
	onSubmit,
	isLoading = false,
	isAnalyzing = false,
	features = [],
	debrand = false,
	resellerLogoUrl,
	isComprehensiveScrape = false,
	handleError,
	tempKey,
	loggedInUser,
	userId,
}) => {
	const clarity = useClarity();
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

	// Slider states
	const [callVolumeIndex, setCallVolumeIndex] = useState(0);
	const [businessLocationsIndex, setBusinessLocationsIndex] = useState(0);
	const [revenueIndex, setRevenueIndex] = useState(0);

	const [referralSource, setReferralSource] = useState<string>(undefined);
	const [referralSourceOther, setReferralSourceOther] = useState<string>('');

	const [currentFeatureIndex, setCurrentFeatureIndex] = useState(-1);
	const [progress, setProgress] = useState(15);
	const [isPolling, setIsPolling] = useState(false);

	const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

	// Sort features by setupOrder
	const sortedFeatures =
		features.length > 0 ? [...features].sort((a, b) => a.setupOrder - b.setupOrder) : [];

	const checkConfigurationStatusMutation = useMutation({
		mutationFn: checkConfigurationStatus,
		onSuccess: (data) => {
			if (data.data?.status === 'completed') {
				if (pollingInterval) {
					clearInterval(pollingInterval);
					setPollingInterval(null);
				}
				setIsPolling(false);

				// Show completion and form
				setProgress(100);
			} else if (data.data?.status === 'in_progress') {
				setIsPolling(true);
				if (!pollingInterval) {
					const interval = setInterval(() => {
						checkConfigurationStatusMutation.mutateAsync(tempKey);
					}, 5000);
					setPollingInterval(interval);
				}
			} else {
				throw new Error('Invalid configuration status');
			}
		},
		onError: (error: Error) => {
			if (pollingInterval) {
				clearInterval(pollingInterval);
				setPollingInterval(null);
			}
			setIsPolling(false);

			console.error('Failed to check configuration status:', error);
			notification.error({
				message: 'Error',
				description: 'There was an error while setting up your AI receptionist. Please try again.',
			});

			// Call the onError callback to navigate back
			handleError?.();
		},
	});

	useEffect(() => {
		return () => {
			if (pollingInterval) {
				clearInterval(pollingInterval);
			}
		};
	}, [pollingInterval]);

	useEffect(() => {
		if (isAnalyzing) {
			// Reset everything when analysis starts
			setProgress(15);
			setCurrentFeatureIndex(-1);
			setIsPolling(false);
			return;
		}

		if (sortedFeatures.length === 0) {
			return;
		}

		// Start building phase after analysis is complete
		const timeouts: NodeJS.Timeout[] = [];

		// Feature animation duration: 6 seconds if comprehensive scrape, 3 seconds otherwise
		const featureDuration = isComprehensiveScrape ? 6000 : 3000;

		// Initial delay before starting features
		timeouts.push(
			setTimeout(() => {
				setProgress(30);
				setCurrentFeatureIndex(0);
			}, 1000),
		);

		// Show each feature with completion animation
		sortedFeatures.forEach((_, index) => {
			const startDelay = 1000 + index * featureDuration;
			const completeDelay = startDelay + featureDuration / 2;

			// Make the feature current (start spinning)
			timeouts.push(
				setTimeout(() => {
					setCurrentFeatureIndex(index);
				}, startDelay),
			);

			// Mark it as completed (show checkmark) and move to next
			timeouts.push(
				setTimeout(() => {
					setProgress(Math.round(30 + ((index + 1) / sortedFeatures.length) * 50)); // Leave 20% for comprehensive scraping

					// Move to next feature if not the last one
					if (index < sortedFeatures.length - 1) {
						setCurrentFeatureIndex(index + 1);
					} else {
						setCurrentFeatureIndex(-1); // No more current features
					}
				}, completeDelay),
			);
		});

		// Start polling after all features are shown to check if background job is complete
		const completionDelay = 1000 + sortedFeatures.length * featureDuration + 1000;
		timeouts.push(
			setTimeout(() => {
				if (isComprehensiveScrape) {
					setProgress(80); // Set to 80% when starting comprehensive scrape
				}
				checkConfigurationStatusMutation.mutateAsync(tempKey);
			}, completionDelay),
		);

		return () => timeouts.forEach(clearTimeout);
	}, [isAnalyzing, sortedFeatures.length, isComprehensiveScrape]);

	const handleSubmit = (values: QuickSetupFormValues) => {
		posthog.capture('submitted-quick-setup-questions-registration-v5');
		clarity('event', 'submitted-quick-setup-questions-registration-v5');

		const hasPrefilledValues = Object.values(values).some((value) => value !== '');

		let finalReferralSource = hasPrefilledValues ? values?.referralSource : referralSource;
		if (finalReferralSource === ReferralSource.Other) {
			finalReferralSource = referralSourceOther;
		}

		// Create form values from slider states
		const formValues: QuickSetupFormValues = {
			callVolume: hasPrefilledValues
				? values?.callVolume
				: callVolumeOptions[callVolumeIndex].value,
			businessLocations: hasPrefilledValues
				? values?.businessLocations
				: locationOptions[businessLocationsIndex].value,
			businessRevenue: hasPrefilledValues
				? values?.businessRevenue
				: revenueOptions[revenueIndex].value,
			interestedFeatures: hasPrefilledValues ? values?.interestedFeatures : selectedFeatures,
			referralSource: finalReferralSource,
		};

		onSubmit(formValues);
	};

	useEffect(() => {
		if (userId && progress === 100) {
			const userCallVolumeIndex =
				callVolumeOptions.findIndex((opt) => opt.value === loggedInUser.callVolume) || 0;
			const userLocationIndex =
				locationOptions.findIndex((opt) => opt.value === loggedInUser.businessLocations) || 1;
			const userRevenueIndex =
				revenueOptions.findIndex((opt) => opt.value === loggedInUser.businessRevenue) || 0;

			setCallVolumeIndex(userCallVolumeIndex);
			setBusinessLocationsIndex(userLocationIndex);
			setRevenueIndex(userRevenueIndex);
			setSelectedFeatures(
				loggedInUser.interestedFeatures || featureOptions.map((option) => option.value),
			);

			setReferralSource(loggedInUser.referralSource || '');
			setReferralSourceOther('');

			handleSubmit({
				callVolume: loggedInUser.callVolume || callVolumeOptions[0].value,
				businessLocations: loggedInUser.businessLocations || locationOptions[0].value,
				businessRevenue: loggedInUser.businessRevenue || revenueOptions[0].value,
				interestedFeatures:
					loggedInUser.interestedFeatures || featureOptions.map((option) => option.value),
				referralSource: loggedInUser.referralSource,
			});
		}
	}, [loggedInUser, progress, userId]);

	const hideQuickSetupQuestions = Boolean(userId && loggedInUser?.businessName) || false;

	// Build Progress and Active Step Block
	const BuildProgressBlock = (
		<div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-white/50 hover:bg-white/95 transition-all duration-300">
			<div className="flex justify-between text-sm mb-3 text-blue-700 font-medium">
				<span>Build Progress</span>
				<span>{progress}%</span>
			</div>
			<div className="h-3 bg-blue-100 rounded-full overflow-hidden">
				<motion.div
					className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
					initial={{ width: 0 }}
					animate={{ width: `${progress}%` }}
					transition={{ type: 'spring', stiffness: 50 }}
				/>
			</div>
			<div className="flex-1 space-y-3 mt-5">
				<AnimatePresence mode="wait">
					{isAnalyzing ? (
						<motion.div
							key="analyzing"
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							className="flex items-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100"
						>
							<motion.div
								animate={{ rotate: 360 }}
								transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
							>
								<Sparkles className="w-5 h-5 text-blue-500" />
							</motion.div>
							<span className="text-sm lg:text-base text-blue-700 font-medium">
								Analyzing your business type...
							</span>
						</motion.div>
					) : isPolling ? (
						<motion.div
							key="polling"
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							className="flex items-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100"
						>
							<motion.div
								animate={{ rotate: 360 }}
								transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
							>
								<Sparkles className="w-5 h-5 text-purple-500" />
							</motion.div>
							<div className="flex flex-col">
								<span className="text-sm lg:text-base text-purple-700 font-medium">
									{isComprehensiveScrape
										? 'Extracting content from your website...'
										: 'Preparing content for your business...'}
								</span>
								<span className="text-xs text-purple-500">
									{isComprehensiveScrape
										? 'This may take a few minutes for comprehensive analysis'
										: 'This will take a few seconds'}
								</span>
							</div>
						</motion.div>
					) : progress === 100 ? (
						<motion.div
							key="completed"
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							className="flex items-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100"
						>
							<CheckCircle className="w-5 h-5 text-green-500" />
							<span className="text-sm lg:text-base text-green-700 font-medium">
								Your AI receptionist is ready! ✨
							</span>
						</motion.div>
					) : currentFeatureIndex >= 0 && sortedFeatures[currentFeatureIndex] ? (
						<motion.div
							key={`feature-${currentFeatureIndex}`}
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 20 }}
							className="flex items-center space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100"
						>
							<motion.div
								animate={{ rotate: 360 }}
								transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
							>
								<Sparkles className="w-5 h-5 text-blue-500" />
							</motion.div>
							<span className="text-sm lg:text-base text-blue-700">
								{sortedFeatures[currentFeatureIndex].description}
							</span>
						</motion.div>
					) : null}
				</AnimatePresence>
			</div>
		</div>
	);

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
			{/* Floating Logo */}
			{(!debrand || (debrand && resellerLogoUrl)) && (
				<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
					<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
						<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
							<img
								src={debrand ? resellerLogoUrl : 'new_logo.png'}
								alt=""
								className={`rounded-lg ${debrand ? 'w-12 h-12' : 'w-6 h-6'}`}
							/>
						</div>
					</div>
				</div>
			)}

			{/* Background Decorative Elements */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
			</div>

			<div className="mt-20 md:mt-0 min-h-screen flex w-full relative z-10">
				{/* Left side - AI Building Progress */}
				<div
					className={`hidden lg:flex bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 flex-col justify-center text-white ${
						hideQuickSetupQuestions ? 'w-full' : 'lg:w-1/2'
					}`}
				>
					<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

					<div className="relative z-10 max-w-lg mx-auto p-6">
						{/* AI Avatar Container */}
						<div className="flex justify-center mb-8">
							<motion.div
								className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-lg flex items-center justify-center border border-white/20"
								animate={{
									boxShadow: [
										'0 0 0 0 rgba(147, 197, 253, 0.4)',
										'0 0 0 15px rgba(147, 197, 253, 0)',
										'0 0 0 0 rgba(147, 197, 253, 0)',
									],
								}}
								transition={{ duration: 2, repeat: Infinity }}
							>
								<Settings className="w-10 h-10 text-white" />
							</motion.div>
						</div>

						{/* Header */}
						<div className="text-center mb-8">
							<h2 className="text-3xl font-bold mb-3 leading-tight">
								Building Your AI Receptionist
							</h2>
							<p className="text-blue-100 text-base leading-relaxed">
								We're building everything while you answer a few quick questions
							</p>
						</div>

						<div className="hidden lg:block">{BuildProgressBlock}</div>
					</div>
				</div>

				{/* Right side - Quick Setup Questions */}
				{!hideQuickSetupQuestions && (
					<div className="w-full lg:w-1/2 flex items-center justify-center p-1">
						<div className="w-full max-w-xl">
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								className="flex-1 flex flex-col"
							>
								{/* Compact Header */}
								<div className="text-center mb-2">
									<h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1">
										Customize Your Receptionist
									</h1>
									<p className="text-gray-600 text-sm">Help us tailor your AI while we build it</p>
								</div>

								{/* Compact Card */}
								<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
									<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />

									<div className="p-2">
										<div className="space-y-0">
											{/* Call Volume Question - Compact */}
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-2 border border-white/50 hover:bg-white/95 transition-all duration-300">
												<label className="block text-gray-700 font-semibold mb-1.5 text-base">
													How many calls do you typically receive per day?
												</label>
												<CustomSlider
													options={callVolumeOptions}
													selectedIndex={callVolumeIndex}
													onChange={setCallVolumeIndex}
													isMobile={true}
													className="px-1"
												/>
											</div>

											{/* Business Locations Question - Compact */}
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-2 border border-white/50 hover:bg-white/95 transition-all duration-300">
												<label className="block text-gray-700 font-semibold mb-1.5 text-base">
													How many business locations do you have?
												</label>
												<CustomSlider
													options={locationOptions}
													selectedIndex={businessLocationsIndex}
													onChange={setBusinessLocationsIndex}
													isMobile={true}
													className="px-1"
												/>
											</div>

											{/* Revenue Question - Compact */}
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-2 border border-white/50 hover:bg-white/95 transition-all duration-300">
												<label className="block text-gray-700 font-semibold mb-1.5 text-base">
													What is your business's annual revenue?
												</label>
												<CustomSlider
													options={revenueOptions}
													selectedIndex={revenueIndex}
													onChange={setRevenueIndex}
													isMobile={true}
													className="px-1"
												/>
											</div>

											{/* Features Question - Enhanced */}
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-2 border border-white/50 hover:bg-white/95 transition-all duration-300">
												<label className="block text-gray-700 font-semibold mb-2 text-base">
													What features are you most interested in? (Select all that apply)
												</label>
												<div className="grid grid-cols-2 gap-2">
													{featureOptions.map((option) => {
														const isSelected = selectedFeatures.includes(option.value);
														const IconComponent = option.icon;
														return (
															<div
																key={option.value}
																className={`relative p-3 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] border-2 ${
																	isSelected
																		? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg border-blue-500'
																		: 'bg-white/80 border-gray-200 hover:border-blue-300 hover:bg-white/95 hover:shadow-md'
																}`}
																onClick={() => {
																	const newValue = isSelected
																		? selectedFeatures.filter((v) => v !== option.value)
																		: [...selectedFeatures, option.value];
																	setSelectedFeatures(newValue);
																}}
															>
																{/* Selection Indicator */}
																<div
																	className={`absolute top-2 right-2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
																		isSelected
																			? 'border-white bg-white'
																			: 'border-gray-300 bg-transparent'
																	}`}
																>
																	{isSelected && (
																		<svg
																			className="w-3 h-3 text-blue-600"
																			fill="none"
																			stroke="currentColor"
																			viewBox="0 0 24 24"
																		>
																			<path
																				strokeLinecap="round"
																				strokeLinejoin="round"
																				strokeWidth={3}
																				d="M5 13l4 4L19 7"
																			/>
																		</svg>
																	)}
																</div>

																{/* Icon */}
																<div className="mb-2">
																	<IconComponent
																		className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-blue-600'}`}
																	/>
																</div>

																{/* Feature Name */}
																<div
																	className={`font-semibold text-sm leading-tight ${isSelected ? 'text-white' : 'text-gray-900'}`}
																>
																	{option.label}
																</div>

																{/* Subtle hover effect indicator */}
																{!isSelected && (
																	<div className="absolute inset-0 bg-blue-50 opacity-0 hover:opacity-20 rounded-xl transition-opacity duration-200" />
																)}
															</div>
														);
													})}
												</div>
											</div>

											<div className="!mb-4 bg-white/90 backdrop-blur-sm rounded-xl p-2 border border-white/50 hover:bg-white/95 transition-all duration-300">
												<label className="block text-gray-700 font-semibold mb-2 text-base">
													How did you hear about us?*
												</label>
												<Select
													placeholder="e.g., Google, Facebook, etc."
													size="large"
													className="w-full"
													value={referralSource}
													onChange={(value) => {
														setReferralSource(value);
														if (value !== ReferralSource.Other) {
															setReferralSourceOther('');
														}
													}}
													options={referralSourceOptions}
												/>

												{referralSource === ReferralSource.Other && (
													<div className="mt-2">
														<Input
															placeholder="Please tell us how you heard about us"
															size="large"
															className="w-full"
															value={referralSourceOther}
															onChange={(e) => setReferralSourceOther(e.target.value)}
															required
														/>
													</div>
												)}
											</div>

											{/* Mobile Build Progress - Compact */}
											<div className="lg:hidden">{BuildProgressBlock}</div>

											{/* Submit Button - Compact */}
											<div className="mt-2">
												<Button
													type="primary"
													size="large"
													loading={progress < 100 || isLoading}
													disabled={
														progress < 100 ||
														!referralSource ||
														(referralSource === ReferralSource.Other && !referralSourceOther)
													}
													className="w-full h-12 rounded-xl font-bold text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
													icon={<ArrowRight className="w-4 h-4" />}
													iconPosition="end"
													onClick={() =>
														handleSubmit({
															callVolume: callVolumeOptions[callVolumeIndex].value,
															businessLocations: locationOptions[businessLocationsIndex].value,
															businessRevenue: revenueOptions[revenueIndex].value,
															interestedFeatures: selectedFeatures,
															referralSource:
																referralSource === ReferralSource.Other
																	? referralSourceOther
																	: referralSource,
														})
													}
												>
													<span className="flex items-center justify-center gap-2">
														{progress < 100 ? 'Please wait...' : '🚀 Complete Setup'}
													</span>
												</Button>
											</div>
										</div>
									</div>
								</Card>
							</motion.div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default QuickSetupQuestions;
